import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import { DOCTOR_PROFILE_FIELDS, DoctorStatus, type DoctorProfile, type DoctorProfileFields } from '~/types/telemedicine'

export const DOCTOR_LANGUAGES = [
    { value: 'ar', label: 'Arabic' },
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'French' },
    { value: 'hy', label: 'Armenian' },
]

type PublishInput = Pick<DoctorProfile, 'user_id' | 'specialty_id' | 'license_number' | 'session_price_usd' | 'full_name'> & {
    specialty_active?: boolean
}

type PublishRequirement = { key: string, label: string, message: string, owner: 'doctor' | 'team', short: string, met: (p: PublishInput) => boolean }

export const PUBLISH_REQUIREMENTS: PublishRequirement[] = [
    { key: 'account', label: 'Doctor account linked', message: 'Doctor account is not linked', owner: 'team', short: 'account', met: (p) => !!p.user_id },
    { key: 'full_name', label: 'Full name', message: 'Full name is required', owner: 'doctor', short: 'full name', met: (p) => !!p.full_name?.trim() },
    { key: 'specialty', label: 'Specialty', message: 'Specialty is required', owner: 'team', short: 'specialty', met: (p) => !!p.specialty_id },
    { key: 'specialty_active', label: 'Specialty is active', message: 'Specialty is inactive', owner: 'team', short: 'specialty', met: (p) => p.specialty_active !== false },
    { key: 'license', label: 'License number', message: 'License number is required', owner: 'doctor', short: 'license number', met: (p) => !!p.license_number?.trim() },
    { key: 'price', label: 'Session price above $0', message: 'Session price must be greater than 0', owner: 'team', short: 'price', met: (p) => Number(p.session_price_usd) > 0 },
]

export function publishBlockers(p: PublishInput): string[] {
    return PUBLISH_REQUIREMENTS.filter((r) => !r.met(p)).map((r) => r.message)
}

export function publishChecklist(p: PublishInput): { key: string, label: string, ok: boolean }[] {
    return PUBLISH_REQUIREMENTS.map((r) => ({ key: r.key, label: r.label, ok: r.met(p) }))
}

export function publishGaps(p: PublishInput): { doctor: string[], team: string[] } {
    const missing = PUBLISH_REQUIREMENTS.filter((r) => !r.met(p))
    return {
        doctor: missing.filter((r) => r.owner === 'doctor').map((r) => r.message),
        team: [...new Set(missing.filter((r) => r.owner === 'team').map((r) => r.short))],
    }
}

export const PUBLISH_INVARIANT_MESSAGE = 'A published doctor needs a specialty, license number and a price above $0. Unpublish first to clear these.'

export function isPublishInvariantError(error?: { code?: string, message?: string } | null): boolean {
    return error?.code === '23514' && /doctor_profiles_published_complete/.test(error.message ?? '')
}

export async function countPublishedDoctors(client: SupabaseClient, specialtyId: string) {
    const { count, error } = await client.from('doctor_profiles')
        .select('id', { count: 'exact', head: true })
        .eq('specialty_id', specialtyId)
        .eq('status', DoctorStatus.published)
    return { count: count ?? 0, error }
}

export function specialtyInUseMessage(count: number): string {
    return `${count} published ${count === 1 ? 'doctor uses' : 'doctors use'} this specialty. Unpublish them or change their specialty first.`
}

export function doctorPortalLink(origin: string, email: string): string {
    return `${origin}/password/verify?email=${encodeURIComponent(email)}`
}

export function isRateLimited(message?: string | null): boolean {
    return /\brate limit|too many|\bseconds\b/i.test(message ?? '')
}

const TRANSITIONS: Record<DoctorStatus, DoctorStatus[]> = {
    [DoctorStatus.draft]: [DoctorStatus.published, DoctorStatus.suspended],
    [DoctorStatus.published]: [DoctorStatus.draft, DoctorStatus.suspended],
    [DoctorStatus.suspended]: [DoctorStatus.draft],
}

export function canTransition(from: DoctorStatus, to: DoctorStatus): boolean {
    return TRANSITIONS[from]?.includes(to) ?? false
}

export function doctorPhotoUrl(supabaseUrl: string, p: Pick<DoctorProfile, 'id' | 'photo_path'>): string | null {
    if (!p.photo_path) return null
    return `${supabaseUrl}/storage/v1/object/public/doctor-photos/${p.id}/${p.photo_path}`
}

export const doctorCreateSchema = z.object({
    path: z.enum(['invite', 'admin_built']),
    full_name: z.string().trim().min(2),
    email: z.string().trim().toLowerCase().email(),
    title: z.string().trim().default('Dr.'),
    specialty_id: z.string().uuid().nullish(),
    license_number: z.string().trim().nullish(),
    bio_en: z.string().nullish(),
    bio_ar: z.string().nullish(),
    languages: z.array(z.string()).default([]),
    years_experience: z.number().int().min(0).max(70).nullish(),
    session_price_usd: z.number().min(0).default(0),
    session_duration_min: z.number().int().min(5).max(180).optional(),
    buffer_min: z.number().int().min(0).max(120).optional(),
    publish: z.boolean().default(false),
})

export type DoctorCreatePayload = z.infer<typeof doctorCreateSchema>

export function pickDoctorFields(p: DoctorProfile): DoctorProfileFields {
    return Object.fromEntries(DOCTOR_PROFILE_FIELDS.map((key) => [key, p[key]])) as DoctorProfileFields
}

export function normalizeDoctorFields(s: DoctorProfileFields): DoctorProfileFields {
    const text = (v: string | null | undefined) => (v ?? '').trim() || null
    return {
        title: s.title?.trim() || 'Dr.',
        full_name: s.full_name.trim(),
        specialty_id: s.specialty_id || null,
        license_number: text(s.license_number),
        years_experience: typeof s.years_experience === 'number' ? s.years_experience : null,
        languages: s.languages ?? [],
        bio_en: text(s.bio_en),
        bio_ar: text(s.bio_ar),
        photo_path: s.photo_path || null,
        session_price_usd: Number(s.session_price_usd) || 0,
        session_duration_min: s.session_duration_min,
        buffer_min: s.buffer_min,
    }
}

export const doctorProfileFormSchema = z.object({
    title: z.string().trim().max(20, 'Keep the title short').nullish(),
    full_name: z.string().trim().min(2, 'Enter the doctor’s full name'),
    license_number: z.string().trim().max(60, 'License number is too long').nullish(),
    years_experience: z.union([z.number().int('Whole years only').min(0).max(70), z.literal(''), z.null()]).optional(),
    session_price_usd: z.number({ invalid_type_error: 'Enter a price' }).min(0, 'Price cannot be negative'),
})

export function changedDoctorFields(before: DoctorProfileFields, after: DoctorProfileFields): Partial<DoctorProfileFields> {
    return Object.fromEntries(DOCTOR_PROFILE_FIELDS
        .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
        .map((key) => [key, after[key]])) as Partial<DoctorProfileFields>
}
